import {
  type CarbonIconProps,
  type CarbonIconType,
  Icon,
} from "@carbon/icons-react";
import { forwardRef, useId } from "react";

/** Where a nested glyph sits in Carbon's 32x32 icon grid. */
export type GlyphBox = { size: number; x: number; y: number };

const BASE_BOX: GlyphBox = { size: 32, x: 0, y: 0 };

/**
 * Overlay box matched to the warning triangle in Carbon's `IncidentReporter`,
 * which lands at ink x 16.05-29.95 / y 16.6-30. A glyph that fills its own
 * viewBox like `WarningAltFilled` (ink 3-29 / 5-30) reproduces that from here.
 * Narrow glyphs come out visually smaller in the same box and need their own,
 * passed as `overlayBox`.
 */
const OVERLAY_BOX: GlyphBox = { size: 17, x: 14.5, y: 14 };

/**
 * Clearance between overlay and base, in grid units. The base is truncated
 * around the overlay with a mask, using a copy of the overlay glyph as the hole —
 * so only the area the overlay actually occupies is cut and the base keeps the
 * rest of its silhouette. Carbon does the same truncation by hand-editing the
 * base path, leaving 3-4 units in `IncidentReporter`.
 *
 * The hole is widened by stroking that copy rather than by scaling it: a stroke
 * is a uniform outward offset, so every overlay gets the same clearance, while
 * scaling would give a tall glyph like the pin a wider gap than a squat one like
 * the triangle.
 */
const OVERLAY_GAP = 2;

// Stroking the hole copy does not reliably clear the inside of the overlay's
// counters (the pin's dot), where the base shows through as a speck. This does:
// a circle over the overlay's core, as a fraction of its box.
const OVERLAY_CORE_RADIUS = 0.25;

/**
 * Combines two Carbon icons into a single icon: the base glyph plus a small
 * overlay glyph in the bottom-right corner, laid out like Carbon's own composed
 * icons (`IncidentReporter`, `TrafficIncident`).
 *
 * Use when the same base glyph appears more than once in one toolbar and needs
 * a modifier to tell the variants apart (e.g. rectangle for a location area vs.
 * rectangle for an alert zone).
 *
 * Carbon icons are `<svg viewBox="0 0 32 32" fill="currentColor">` that spread
 * unknown props, so both glyphs nest as SVG elements in the 32x32 grid — no
 * path data is copied and nothing drifts when Carbon updates a glyph. The
 * result is a `CarbonIconType`, so it is accepted anywhere an `icon` prop is.
 *
 * The gap around the overlay is a mask rather than a shape in the background
 * color, so it holds up on any surface and in both themes.
 *
 * The overlay renders at roughly half the base size, so pass a `Filled` variant
 * (`LocationFilled`, not `Location`) — outline glyphs turn to mush below ~10px.
 */
export function createOverlayIcon(
  Base: CarbonIconType,
  Overlay: CarbonIconType,
  options: {
    name: string;
    overlayBox?: GlyphBox;
  },
): CarbonIconType {
  const overlay = options.overlayBox ?? OVERLAY_BOX;
  // Stroke width is in the overlay's own scaled-down units, and half of a stroke
  // falls outside the shape, so this is OVERLAY_GAP of outward offset.
  const gapStrokeWidth = (2 * OVERLAY_GAP * 32) / overlay.size;

  const Composed = forwardRef<SVGSVGElement, CarbonIconProps>(
    function OverlayIcon({ size = 16, className, children, ...rest }, ref) {
      const maskId = useId();

      return (
        <Icon
          ref={ref}
          width={size}
          height={size}
          viewBox="0 0 32 32"
          fill="currentColor"
          className={className}
          {...rest}
        >
          <mask
            id={maskId}
            maskUnits="userSpaceOnUse"
            x={0}
            y={0}
            width={32}
            height={32}
          >
            <rect x={0} y={0} width={32} height={32} fill="white" />
            <Overlay
              {...overlay}
              fill="black"
              stroke="black"
              strokeWidth={gapStrokeWidth}
              strokeLinejoin="round"
              // A nested svg clips to its own viewport, which would cut the
              // stroke off wherever the glyph reaches the edge of its viewBox.
              style={{ overflow: "visible" }}
            />
            <circle
              cx={overlay.x + overlay.size / 2}
              cy={overlay.y + overlay.size / 2}
              r={overlay.size * OVERLAY_CORE_RADIUS}
              fill="black"
            />
          </mask>
          <g mask={`url(#${maskId})`}>
            <Base {...BASE_BOX} />
          </g>
          <Overlay {...overlay} />
          {children}
        </Icon>
      );
    },
  );

  Composed.displayName = options.name;
  return Composed;
}
